import { useEffect, useRef, useState } from 'react';

/**
 * Scroll-aware behavior for BoardView.
 *
 * Tracks vertical scrolling within a board's scroll container and exposes:
 *  - `collapsed`: true once the user has scrolled past COLLAPSE_THRESHOLD.
 *    Used to collapse the in-page header and to swap the subnav title for
 *    an inline editable input.
 *  - `scrollRef`: ref to attach to the board's scroll container.
 */

const COLLAPSE_THRESHOLD = 24;

interface ScrollBehavior {
  collapsed: boolean;
  scrollRef: React.MutableRefObject<HTMLElement | null>;
}

export function useBoardScrollBehavior(): ScrollBehavior {
  const [collapsed, setCollapsed] = useState(false);
  const scrollRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) {
      return undefined;
    }

    let rafPending = false;

    const apply = () => {
      rafPending = false;
      setCollapsed(el.scrollTop > COLLAPSE_THRESHOLD);
    };

    const handleScroll = () => {
      if (rafPending) {
        return;
      }
      rafPending = true;
      requestAnimationFrame(apply);
    };

    el.addEventListener('scroll', handleScroll, { passive: true });
    apply();

    return () => {
      el.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return { collapsed, scrollRef };
}
